import { Navigation } from "@/components/ui/navigation";
import { Link } from "react-router-dom";
import { Shield, FileText, Mail } from "lucide-react";

export default function TermsPrivacy() {
  return (
    <div className="min-h-screen bg-soft-gradient text-neutral-900">
      <Navigation />
      
      <main className="flex flex-col">
        {/* Hero Section */}
        <section className="relative pt-12 pb-8 sm:pt-16 sm:pb-12">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10">
                <Shield className="h-8 w-8 text-primary" />
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Terms of Service & Privacy Policy
            </h1>
            
            <p className="mt-4 text-sm text-neutral-500">
              Last updated: January 10, 2026
            </p>
          </div>
        </section>

        {/* Navigation Pills */}
        <section className="pb-8">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3">
              <a 
                href="#terms" 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-neutral-200 text-sm font-medium text-neutral-700 hover:bg-white hover:shadow-sm transition-all"
              >
                <FileText className="h-4 w-4" />
                Terms of Service
              </a>
              <a 
                href="#privacy" 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-neutral-200 text-sm font-medium text-neutral-700 hover:bg-white hover:shadow-sm transition-all"
              >
                <Shield className="h-4 w-4" />
                Privacy Policy
              </a>
            </div>
          </div>
        </section>

        {/* Terms of Service Section */}
        <section id="terms" className="py-8 sm:py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-white/80 border border-neutral-200 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Terms of Service
                </h2>
              </div>

              <div className="space-y-8 text-neutral-700">
                {/* 1. Introduction */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">1. Introduction</h3>
                  <p className="leading-relaxed">
                    Welcome to our AP study platform ("<span className="font-semibold">Lucen</span>"). By accessing or using this Website, you agree to be bound by these Terms of Service. If you do not agree, please do not use the Website.
                  </p>
                  <p className="leading-relaxed mt-2">
                    This Website is operated by student founders and is intended solely for educational purposes.
                  </p>
                </div>

                {/* 2. Eligibility */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">2. Eligibility</h3>
                  <p className="leading-relaxed">
                    The Website is intended for students, educators, and individuals seeking educational resources. If you are under 13 years old, you may only use the Website with parental or guardian consent.
                  </p>
                </div>

                {/* 3. Use of the Website */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">3. Use of the Website</h3>
                  <p className="leading-relaxed mb-3">
                    You agree to use the Website only for lawful and educational purposes. You may not:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 ml-2">
                    <li>Copy, sell, or redistribute content for commercial use</li>
                    <li>Attempt to hack, disrupt, or misuse the platform</li>
                    <li>Upload malicious code or spam</li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    All content is provided for personal, non-commercial study use only.
                  </p>
                </div>

                {/* 4. Educational Disclaimer */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">4. Educational Disclaimer</h3>
                  <p className="leading-relaxed">
                    The Website provides study materials, practice questions, and notes based on AP course frameworks. We are <span className="font-semibold">not affiliated with the College Board</span>, and use of this Website does not guarantee any specific exam score or outcome.
                  </p>
                </div>

                {/* 5. Intellectual Property */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">5. Intellectual Property</h3>
                  <p className="leading-relaxed">
                    All original content, including questions, notes, and platform design, is owned by the Website creators unless otherwise stated. You may not reproduce or distribute content without permission.
                  </p>
                </div>

                {/* 6. Limitation of Liability */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">6. Limitation of Liability</h3>
                  <p className="leading-relaxed mb-3">
                    The Website is provided "as is." We are not responsible for:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 ml-2">
                    <li>Errors or inaccuracies in content</li>
                    <li>Website downtime or technical issues</li>
                    <li>Any outcomes resulting from use of the materials</li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    Use of the Website is at your own risk.
                  </p>
                </div>

                {/* 7. Changes to These Terms */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">7. Changes to These Terms</h3>
                  <p className="leading-relaxed">
                    We may update these Terms at any time. Continued use of the Website after changes means you accept the updated Terms.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Policy Section */}
        <section id="privacy" className="py-8 sm:py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-white/80 border border-neutral-200 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-100">
                  <Shield className="h-5 w-5 text-emerald-600" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Privacy Policy
                </h2>
              </div>

              <div className="space-y-8 text-neutral-700">
                {/* 1. Information We Collect */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">1. Information We Collect</h3>
                  <p className="leading-relaxed mb-3">
                    We may collect limited information to improve the learning experience, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 ml-2">
                    <li>Basic account information (if applicable)</li>
                    <li>Usage data such as progress and question performance</li>
                    <li>Anonymous analytics (e.g., page visits, feature usage)</li>
                  </ul>
                  <p className="leading-relaxed mt-3 font-medium text-neutral-800">
                    We <span className="font-semibold">do not collect sensitive personal information</span>.
                  </p>
                </div>

                {/* 2. How We Use Information */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">2. How We Use Information</h3>
                  <p className="leading-relaxed mb-3">
                    Information is used to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 ml-2">
                    <li>Personalize learning and identify weak areas</li>
                    <li>Improve platform performance and features</li>
                    <li>Maintain Website security</li>
                  </ul>
                  <p className="leading-relaxed mt-3 font-medium text-neutral-800">
                    We do <span className="font-semibold">not sell or rent user data</span>.
                  </p>
                </div>

                {/* 3. Cookies */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">3. Cookies</h3>
                  <p className="leading-relaxed mb-3">
                    We may use cookies or similar technologies to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 ml-2">
                    <li>Keep users logged in</li>
                    <li>Understand how the Website is used</li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    You can disable cookies in your browser settings, though some features may not function properly.
                  </p>
                </div>

                {/* 4. Data Sharing */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">4. Data Sharing</h3>
                  <p className="leading-relaxed mb-3">
                    We do not share personal data with third parties except:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 ml-2">
                    <li>When required by law</li>
                    <li>To protect the safety and integrity of the platform</li>
                  </ul>
                </div>

                {/* 5. Data Security */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">5. Data Security</h3>
                  <p className="leading-relaxed">
                    We take reasonable measures to protect user data. However, no online platform can guarantee complete security.
                  </p>
                </div>

                {/* 6. Children's Privacy */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">6. Children's Privacy</h3>
                  <p className="leading-relaxed">
                    We do not knowingly collect personal data from children under 13 without parental consent. If such data is discovered, it will be deleted promptly.
                  </p>
                </div>

                {/* 7. Changes to This Policy */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">7. Changes to This Policy</h3>
                  <p className="leading-relaxed">
                    This Privacy Policy may be updated periodically. Updates will be posted on this page.
                  </p>
                </div>

                {/* 8. Contact Us */}
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">8. Contact Us</h3>
                  <p className="leading-relaxed mb-3">
                    If you have questions about these Terms or the Privacy Policy, please contact us at:
                  </p>
                  <a 
                    href="mailto:serikanj.lucen@gmail.com"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    serikanj.lucen@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Acknowledgment Banner */}
        <section className="py-8 sm:py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary/5 border border-primary/10 p-6 sm:p-8 text-center">
              <p className="text-neutral-700 leading-relaxed">
                <span className="font-semibold">By using this Website</span>, you acknowledge that you have read and understood these Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer role="contentinfo" className="mt-10 border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-neutral-500">
            <p>© 2026 Lucen — All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
